// src/composables/useCustomerSse.js
import { ref, onUnmounted } from "vue";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import userUtils from "@/common/userUtils";
import { logDebug, logError, logInfo } from "@/utils/logHelper";
import { useAlertsStore } from "@/store/notifications/useAlertsStore";

export function useCustomerSse(onEstimateUpdated) {
  const isConnected = ref(false);
  let sseController = null;
  const alertsStore = useAlertsStore();

  // 디버그 토글: localStorage.setItem('sse:debug','1') 로 켜기
  const debugEnabled = () =>
    typeof localStorage !== "undefined" &&
    localStorage.getItem("sse:debug") === "1";
  const dbg = (...args) => {
    // warn을 쓰면 콘솔 필터(Verbose/Info) 영향이 적음
    if (debugEnabled()) console.warn("[CustomerSSE]", ...args);
  };

  async function connect() {
    // 이미 연결 중이면 스킵
    if (sseController) return;

    const token = userUtils.getAccessToken();
    if (!token) {
      logDebug("[CustomerSSE] 토큰 없음 - 연결 스킵");
      dbg("connect skip: no token", userUtils?.getDebugInfo?.());
      return;
    }

    sseController = new AbortController();

    try {
      dbg("connecting…", {
        userSuid: userUtils?.getUserInfo?.()?.userSuid ?? null,
      });
      const SSE_BASE_URL = process.env.VUE_APP_API_URL || "";
      await fetchEventSource(`${SSE_BASE_URL}/api/authn/sse/subscribe`, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          "X-ACCESS-TOKEN": token,
        },
        signal: sseController.signal,
        onopen(response) {
          if (response.ok) {
            isConnected.value = true;
            logInfo("[CustomerSSE] 연결 완료");
            dbg("open ok", { status: response.status });
          } else {
            dbg("open not ok", { status: response.status });
          }
        },
        onmessage(e) {
          // ✅ 무조건 찍히는 로그 추가
          console.log("🔥 SSE onmessage 호출됨!", e.event, e.data);
          dbg("message", { event: e.event, data: e.data });

          if (e.event === "ESTIMATE_UPDATED") {
            logDebug("[CustomerSSE] 견적 수정 알림 수신:", e.data);
            try {
              const data = JSON.parse(e.data);
              if (typeof onEstimateUpdated === "function") {
                onEstimateUpdated(data);
              }
            } catch (err) {
              logError("[CustomerSSE] 이벤트 파싱 오류:", err);
            }
            return;
          }

          if (e.event === "CUSTOMER_ALERT") {
            try {
              const data = JSON.parse(e.data);
              const title = data?.title || "알림";
              const body = data?.body || "";
              const link = data?.link || null;
              alertsStore.pushInboxMessage({
                title,
                body,
                type: "info",
                link,
              });
            } catch (err) {
              logError("[CustomerSSE] CUSTOMER_ALERT 파싱 오류:", err);
            }
          }
        },
        onerror(err) {
          isConnected.value = false;
          logError("[CustomerSSE] 연결 오류 - 5초 후 재연결");
          dbg("error", err?.name, err?.message);
          // AbortController로 정상 종료된 경우는 재연결 안 함
          if (err?.name === "AbortError") return;
          sseController = null;
          setTimeout(connect, 5000);
          throw err;
        },
        openWhenHidden: true, // 탭 숨겨도 연결 유지
      });
    } catch (err) {
      if (err?.name !== "AbortError") {
        logError("[CustomerSSE] 연결 오류:", err);
        dbg("connect catch", err?.name, err?.message);
      }
    }
  }

  function disconnect() {
    sseController?.abort();
    sseController = null;
    isConnected.value = false;
    logInfo("[CustomerSSE] 연결 종료");
  }

  onUnmounted(disconnect);

  return { connect, disconnect, isConnected };
}
